import { UserRoutes, getOrigin } from 'client/routes/RouteGenerator';
import Constants from 'client/util/Constants';
import CsrfApi from 'client/api/CsrfApi';
import LoginApi from 'client/api/LoginApi';
import LoginConsts from 'client/util/Constants/LoginConstants';
import Util from 'client/util/Util';
import axios from 'axios';
import loginStore from 'client/stores/loginStore';

const { userConsoleRootRoute } = LoginConsts;
const { user } = LoginConsts.clientType;
class UserLoginApi extends LoginApi {
  constructor(_userRoutes = UserRoutes) {
    super(Constants.xsrf);
    this.authDuoUrl = `${getOrigin()}/userconsole/auth/duo`;
    this.UserRoutes = _userRoutes;
    // TEMP: Use Duo auth server running locally
    // this.authDuoUrl = 'http://localhost:8005/auth/duo';
  }

  /**
   * All POSTs to verify a factor must include the state ID if it is available.
   * If the state ID is available and is not included then users who are in a
   * "Stepup Auth Event" will not be able to successfully authenticate.
   *
   * @param {string} url
   * @param {object} payload
   * @returns Axios POST promise
   * @memberof UserLoginApi
   */
  mfaVerificationPost(url, payload = {}, store = loginStore) {
    const stateId = store.getters['StepUpModel/stateId'];
    const request = payload;

    if (stateId) {
      // state is specific to sso step up and should be cleared after step up failure
      // if this is in the payload for regular login, it isn't being cleared correctly
      request.state = stateId;
    }

    return this.axios.post(url, request);
  }

  /**
   * Activate a user
   * @param  {Object} payload [firstName, lastName, password, confirm, preserve]
   * @return {Object}         [returns the promise from the axios request]
   */
  activate(payload, key) {
    return this.axios.post(`/activate/${key}`, payload);
  }

  /**
   * Primary authentication for a user.
   * @param  {Object} payload [email, password]
   * @return {AxiosPromise} [returns the promise from the axios request]
   */
  login(payload) {
    const path = LoginConsts.authURLs[user];
    return this.axios.post(path, payload);
  }

  /**
   * Email verification for a user.
   * @param  {Object} payload [email]
   * @return {AxiosPromise} [returns the promise from the axios request]
   */
  submitEmail(payload) {
    const path = LoginConsts.authURLs.submitEmail;
    return this.axios.post(path, payload);
  }

  /**
   * Authentication for a DURT session.
   * @param  {Object} payload [sessionToken, sessionTokenCode]
   * @return {AxiosPromise} [returns the promise from the axios request]
   */
  loginDURT(payload) {
    const path = LoginConsts.authURLs.durt;
    return this.mfaVerificationPost(path, payload);
  }

  /**
   * Logout of a DURT session.
   * @return {AxiosPromise} [returns the promise from the axios request]
   */
  logoutDURT() {
    const path = LoginConsts.authURLs.durt;
    return this.axios.delete(path);
  }

  /**
   * MFA authentication for a user.
   *
   * @TODO This endpoint should be renamed "/auth/totp". As of December 2019, there are multiple
   * methods for MFA.
   * @param  {Object} payload [otp: token to be verified]
   * @return {AxiosPromise} [returns the promise from the axios request]
   */
  verifyTotp(payload) {
    return this.mfaVerificationPost(`${userConsoleRootRoute}/auth/totp`, payload);
  }

  /**
   * OTP authentication for a user.
   * @param  {Object} payload - Payload object
   * @param  {string} payload.otp - Token to be verified
   * @param  {string} payload.sessionId - Session identifier from sendOtp
   * @return {AxiosPromise} [returns the promise from the axios request]
   */
  verifyOtp(payload) {
    return this.mfaVerificationPost(`${userConsoleRootRoute}/auth/otp/verify`, payload);
  }

  /**
   * Send OTP to a user (login or SSO step-up when StepUpModel state is set).
   * @return {AxiosPromise} [returns the promise from the axios request]
   */
  sendOtp() {
    return this.mfaVerificationPost(`${userConsoleRootRoute}/auth/otp/send`, {});
  }

  /**
  * Requests for a password change. Upon success the user will
  * receive an email.
  * @param  {Object} payload [contains email that will receive the email]
  * @return {AxiosPromise} [returns the promise from the axios request]
  */
  requestPasswordReset(payload) {
    return this.axios.post('/changepassword', payload);
  }

  /**
   * Pre-flight MFA eligibility probe for MFA-direct password reset (NA-4357).
   * @param  {Object} payload { email }
   * @return {AxiosPromise}
   */
  checkMfaEligibility(payload) {
    return this.axios.post('/changepassword/mfa-eligibility', payload);
  }

  /**
   * Mint pwdResetToken for MFA-direct password reset session (NA-4357).
   * @param  {Object} payload { email }
   * @return {AxiosPromise}
   */
  initMfaReset(payload) {
    return this.axios.post('/changepassword/mfa-init', payload);
  }

  requestDeviceCode() {
    return this.axios.post(
      `${this.UserRoutes.deviceCert()}auth/code`,
      undefined,
      { withCredentials: true },
    );
  }

  getDuoAuth() {
    return this.axios.get(this.authDuoUrl);
  }

  /**
   * Request a user's data.
   * @return {Object} [returns the promise from the axios request]
   */
  // eslint-disable-next-line class-methods-use-this
  getUser() {
    // not using this.axios because we don't want the xsrf
    return axios.get(`${userConsoleRootRoute}/api/self`).catch(async (err) => {
      // /api/self has side-effects in that it will destroy the current cookie.
      // We need to make sure we re-establish a cookied session
      // before we do future API calls within this app.
      // Otherwise, we could accidentally end up with a race condition
      // of multiple other API calls setting different cookies.
      try {
        await CsrfApi.confirmCsrf(this.csrfUrl, this.csrfHeader, this.axios.defaults, this.axios);
      } catch (csrfErr) {
        // Preserve both errors by chaining or attaching the original error
        csrfErr.originalError = err;
        throw csrfErr;
      }
      throw err;
    });
  }

  /**
  * Resets a user's password. Upon success the user will receive an email.
  * @param  {String} key [the confirmation key for resetting the password]
  * @param  {Object} payload
  * @param  {String} payload.password [The user's password]
  * @param  {String} payload.confirm [The user's password again]
  * @return {AxiosPromise} [returns the promise from the axios request]
  */
  resetPassword(key, payload) {
    return this.axios.post(`/changepassword/${key}`, payload);
  }

  /**
   * Send SMS OTP during password reset MFA.
   * @param  {String} key Activation or recovery reset key
   * @param  {Object} payload Must include pwdResetToken from the MFA challenge response
   * @return {AxiosPromise}
   */
  sendPasswordResetSmsOtp(key, payload) {
    return this.axios.post(`/changepassword/${key}/sms/send`, payload);
  }

  validateDeviceCert() {
    // This POST does not send any data, hence undefined. null below would send
    // a body with a content-type and there's no reason for us to do this with
    // this API.
    // not using this.axios because we don't want the xsrf
    return axios.post(
      `${this.UserRoutes.deviceCert()}auth`,
      undefined,
      {
        withCredentials: true,
      },
    );
  }

  /**
   * Get the challenge used for WebAuthn Authentication
   */
  getWebAuthnChallenge() {
    return this.axios.get(`${userConsoleRootRoute}/auth/webauthn`)
      .then((resp) => resp.data);
  }

  /**
   * Posts the WebAuthn signed credential for verification.
   */
  postWebAuthnSignature({ publicKeyCredential, token }) {
    return this.mfaVerificationPost(`${userConsoleRootRoute}/auth/webauthn`, { publicKeyCredential, token })
      .then((resp) => resp.data);
  }

  /**
   * Posts the Duo signature and token to verify
   * @param  {Object} payload [contains the signature request and token to be verified]
   */
  postDuoSignature(payload) {
    return this.mfaVerificationPost(`${this.authDuoUrl}`, payload);
  }

  postDuoChallengeUrl(payload) {
    return this.mfaVerificationPost(`${this.authDuoUrl}/oauth/challenge`, payload);
  }

  oauthPostChangeRequest() {
    const redirectToParam = Util.getQueryStringParameter('redirectTo');
    const redirectToUrl = new URL(redirectToParam);
    const requestParams = Util.getQueryStringParameter('request_params', redirectToUrl.search);
    const url = `/userconsole/oauth2/change-identity/?request_params=${requestParams}`;

    // request interceptor will automagically send user to login with URL returned in header
    return this.axios.post(url, {});
  }

  /**
   * Posts the Managed Chrome signed credential for verification.
   */
  async validateManagedChrome(token) {
    const path = `${userConsoleRootRoute}/auth/managedchrome`;
    const payload = { token };

    try {
      return await this.axios.post(path, payload);
    } catch (error) {
      const data = error?.response?.data;
      const message = data?.message || data?.error || (typeof data === 'string' ? data : '');
      if (
        message === 'Invalid XSRF cookie'
        || message === LoginConsts.responseMessages.invalidCsrf
        || message === LoginConsts.responseMessages.invalidCsrfOidc
      ) {
        this.clearCsrfToken();
        return this.axios.post(path, payload);
      }
      throw error;
    }
  }

  /**
   * Fetches the sanitized error detail payload for the parameterized {type, code}
   * pair extracted from the redirect URL.
   */
  fetchErrorDetail(type, code) {
    return this.axios.get(`${userConsoleRootRoute}/api/error-detail`, {
      params: { type, code },
    })
      .then((response) => response.data);
  }
}

export default new UserLoginApi();
export const UserLoginApiHelper = UserLoginApi;
